/**
 * What an hour of work costs the employer.
 *
 * From the designer's report, 2026-09-21: "Standartinis arbeidsgiveravgift yra 14,1 % nuo
 * išmokėto atlyginimo ir kitų išmokų. Pensija minimum 2 %. Manau, kuriant darbo vietą turėtų
 * būti galima pasirinkti pensijos procentą." The employer's contribution is 14.1% of pay, and
 * pension is at least 2%; most companies use the minimum, so the rate belongs to the company
 * rather than to the code.
 *
 * Before this, `AGA = 1.141` was a constant in shell/local.ts and pension did not exist at all,
 * so every labour figure in the product was about 2% light.
 *
 * Both percentages are applied to gross pay, not compounded: 14.1% employer's contribution and
 * 2% pension on 1000 kr is 1000 + 141 + 20, not 1000 x 1.141 x 1.02. Obligatorisk
 * tjenestepensjon is a percentage of salary, and arbeidsgiveravgift is charged on the pension
 * contribution too, which is why aga applies to the pension as well.
 */

export interface Rates {
  /** Arbeidsgiveravgift as a percentage. Sone 1 is 14.1. */
  agaPct: number;
  /** Obligatorisk tjenestepensjon as a percentage. The legal minimum is 2. */
  pensionPct: number;
  /**
   * Feriepenger as a percentage of pay: 10.2 by ferieloven §10(2), 12 with a tariff's fifth week.
   * Added 2026-09-22 (CV7, owner): the figure is sold as what an hour costs, and without it every
   * labour figure ran about 11.6 % light.
   */
  holidayPct: number;
}

export const DEFAULT_RATES: Rates = { agaPct: 14.1, pensionPct: 2, holidayPct: 10.2 };

/** Norway's zones, for when a company is not in sone 1. Kept as data, not a dropdown yet. */
export const AGA_ZONES: [string, number][] = [
  ['Sone 1', 14.1],
  ['Sone 1a', 14.1],
  ['Sone 2', 10.6],
  ['Sone 3', 6.4],
  ['Sone 4', 5.1],
  ['Sone 4a', 7.9],
  ['Sone 5', 0],
];

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Sane rates whatever is stored.
 *
 * `num()` is not enough here: it turns anything unreadable into 0, and 0 is a PERFECTLY VALID
 * rate, sone 5 pays no employer contribution at all. So rubbish in storage would have been
 * silently read as "this company pays nothing" rather than falling back. The finite check has to
 * happen before the range check, not after.
 */
const pick = (v: unknown, fallback: number): number => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : fallback;
};

export function ratesOf(stored: Partial<Rates> | null | undefined): Rates {
  return {
    agaPct: pick(stored?.agaPct, DEFAULT_RATES.agaPct),
    pensionPct: pick(stored?.pensionPct, DEFAULT_RATES.pensionPct),
    holidayPct: pick(stored?.holidayPct, DEFAULT_RATES.holidayPct),
  };
}

export interface Cost {
  /** Gross pay for the hours worked. */
  wage: number;
  /** Feriepenger earned on the wage. */
  holiday: number;
  /** On the wage. */
  pension: number;
  /** Charged on wage, feriepenger AND pension, which is how arbeidsgiveravgift works. */
  aga: number;
  /** What it actually costs: wage + feriepenger + pension + aga. */
  total: number;
}

export function costOf(wage: number, rates: Rates): Cost {
  const w = num(wage);
  const holiday = w * (rates.holidayPct / 100);
  const pension = w * (rates.pensionPct / 100);
  const aga = (w + holiday + pension) * (rates.agaPct / 100);
  return { wage: w, holiday, pension, aga, total: w + holiday + pension + aga };
}

export interface Paid {
  hourly_rate?: string | number | null;
  monthly_rate?: string | number | null;
  contract_type?: string | null;
  max_hours_per_week?: number | null;
}

/**
 * What one hour of this person's time is paid, gross.
 *
 * A monthly-salaried person has no hourly rate, and the labour figures counted them as free:
 * `hourly_rate ?? 0`. Nobody in the report mentioned it, which is what you would expect, because
 * the number was simply too low rather than obviously wrong. Their monthly salary is spread over
 * the agreed week: monthly x 12 / 52 / weekly hours. That is an approximation, and it is a much
 * better one than zero.
 *
 * Returns 0 when there is nothing to go on, which is honest: a person with no rate recorded
 * cannot be costed, and pretending otherwise would make the total look complete.
 */
export function hourlyRateOf(u: Paid | null | undefined): number {
  if (!u) return 0;
  const hourly = num(u.hourly_rate);
  if (hourly > 0) return hourly;
  const monthly = num(u.monthly_rate);
  const week = num(u.max_hours_per_week);
  if (monthly > 0 && week > 0) return (monthly * 12) / 52 / week;
  return 0;
}
